/// <amd-module name="TFS/DRITools/Components/UsageInsights/TimeRangeForm" />
import * as React from "react";

// Office Fabric
import { Label } from "OfficeFabric/Label";

// VSS
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

// VSSUI
import { DateTimePicker } from "VSSUI/DateTimePicker";
import { IPickListItem, IPickListSelection, PickListDropdown } from "VSSUI/PickList";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";

// Resources
import * as Resources from "TFS/DRITools/Resources";

// DRITools
import { parseDateAMPM } from "TFS/DRITools/util/Date";

/**
 * State of the TimeRangeForm, contains startTime, endTime and if timeRange is open
 */
export interface ITimeRangeFormState extends IVssComponentProperties {
    startTime: Date;
    endTime: Date;
    timeRangeOpen: boolean;
}

/**
 * Initial startTime and endTime passed
 */
export interface ITimeRangeFormProps extends IVssComponentProperties {
    startTime: Date;
    endTime: Date;
}

export class TimeRangeForm extends VssComponent<ITimeRangeFormProps, ITimeRangeFormState> {
    private _timeRanges: string[] = [
        Resources.UsageInsights_LastHour,
        Resources.UsageInsights_Last4Hours,
        Resources.UsageInsights_Last24Hours,
        Resources.UsageInsights_Last7days,
        Resources.UsageInsights_Last28days,
        Resources.UsageInsights_Custom,
        Resources.UsageInsights_StartTime,
        Resources.UsageInsights_EndTime
    ];
    private _chosenRange: string[] = [Resources.UsageInsights_LastHour];

    private _inputChange = {
        startTime: (newStartTime: Date): void => {
            this.setState({ startTime: newStartTime });
        },
        endTime: (newEndTime: Date): void => {
            this.setState({ endTime: newEndTime });
        },
        timeRange: (selection: IPickListSelection): void => {
            const pick = selection.selectedItems[0];
            let startTime!: Date;
            const endTime: Date = new Date();
            if (pick === Resources.UsageInsights_LastHour) {
                startTime = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate(), endTime.getHours() - 1, endTime.getMinutes());
            } else if (pick === Resources.UsageInsights_Last4Hours) {
                startTime = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate(), endTime.getHours() - 4, endTime.getMinutes());
            } else if (pick === Resources.UsageInsights_Last24Hours) {
                startTime = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate() - 1, endTime.getHours(), endTime.getMinutes());
            } else if (pick === Resources.UsageInsights_Last7days) {
                startTime = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate() - 7, endTime.getHours(), endTime.getMinutes());
            } else if (pick === Resources.UsageInsights_Last28days) {
                startTime = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate() - 28, endTime.getHours(), endTime.getMinutes());
            } else {
                this._chosenRange[0] = Resources.UsageInsights_Custom;
                return;
            }
            this._chosenRange = [pick];
            this.setState({ timeRangeOpen: false });
            this.setState({ startTime: startTime, endTime: endTime, timeRangeOpen: true });
        },
        toggleTimeRange: (): void => {
            this.setState({ timeRangeOpen: !this.state.timeRangeOpen });
        }
    };

    constructor(props: ITimeRangeFormProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            startTime: props.startTime,
            endTime: props.endTime,
            timeRangeOpen: false
        };
    }

    public getStartTime(): Date {
        const { startTime } = this.state;
        return startTime;
    }

    public getEndTime(): Date {
        const { endTime } = this.state;
        return endTime;
    }

    public render(): JSX.Element {
        const { timeRangeOpen, startTime, endTime } = this.state;

        return (
            <PickListDropdown
                getPickListItems={() => {
                    if (this._chosenRange[0] === Resources.UsageInsights_Custom) {
                        return this._timeRanges;
                    }
                    return this._timeRanges.slice(0, 6);
                }}
                pickListClassName="usage-callout"
                selectedItems={this._chosenRange}
                onSelectionChanged={this._inputChange.timeRange}
                onRenderItemText={this._renderTimePicker}
                pickListWidth={350}
                dropdownIconProps={{}}
                isOpen={timeRangeOpen}
                width={350}
                selectedItemIndicators={[
                    {
                        getItemIndicator: (item: any) => {
                            const iconName = timeRangeOpen ? "ChevronUp" : "ChevronDown";
                            return {
                                onRender: () => {
                                    return (
                                        <div className="header-div flex-row" onClick={this._inputChange.toggleTimeRange}>
                                            <VssIcon className="chevron" iconName={iconName} iconType={VssIconType.fabric} />
                                            <span>
                                                {item === Resources.UsageInsights_Custom
                                                    ? parseDateAMPM(startTime) + " - " + parseDateAMPM(endTime)
                                                    : item}
                                            </span>
                                        </div>
                                    );
                                }
                            };
                        }
                    }
                ]}
            />
        );
    }

    private _renderTimePicker = (item: IPickListItem): JSX.Element => {
        const { startTime, endTime } = this.state;
        if (item.key === Resources.UsageInsights_StartTime || item.key === Resources.UsageInsights_EndTime) {
            const isStart: boolean = item.key === Resources.UsageInsights_StartTime;
            return (
                <div className="flex-row">
                    <div className="startend-label">
                        <Label>{item.key}</Label>
                    </div>
                    <div>
                        <DateTimePicker
                            defaultValue={isStart ? startTime : endTime}
                            onValueChanged={isStart ? this._inputChange.startTime : this._inputChange.endTime}
                            formatTime={date => {
                                return date.getHours() + ":" + date.getMinutes().toString();
                            }}
                            parseTimeFromString={(timeStr: string): Date => {
                                const parts: string[] = timeStr.split(":");
                                let parsedTime: Date | null = null;
                                if (parts.length === 2) {
                                    if (Number(parts[0]) >= 0 && Number(parts[0]) < 24 && Number(parts[1]) >= 0 && Number(parts[1]) < 60) {
                                        parsedTime = new Date(0, 0, 0, Number(parts[0]), Number(parts[1]), 0, 0);
                                    }
                                }
                                return parsedTime as Date;
                            }}
                        />
                    </div>
                </div>
            );
        }
        return <span>{item.key}</span>;
    };
}
